const FORM_SCROLL_KEY = 'kaleos:form-scroll-y';

const restoreSavedFormScroll = () => {
  try {
    const saved = sessionStorage.getItem(FORM_SCROLL_KEY);
    if (saved === null) return;

    const y = Number(saved);
    if (Number.isNaN(y)) {
      sessionStorage.removeItem(FORM_SCROLL_KEY);
      return;
    }

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    if (window.location.hash) {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    window.scrollTo(0, y);
    sessionStorage.removeItem(FORM_SCROLL_KEY);
  } catch (error) {}
};

restoreSavedFormScroll();
window.addEventListener('pageshow', restoreSavedFormScroll);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const scrollY = window.scrollY;

    if (typeof form.action === 'string' && form.action.includes('#')) {
      form.action = form.action.split('#')[0];
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const restoreScroll = () => window.scrollTo(0, scrollY);
    restoreScroll();
    requestAnimationFrame(restoreScroll);
    setTimeout(restoreScroll, 50);
    setTimeout(restoreScroll, 300);

    queueMicrotask(() => {
      if (event.defaultPrevented) return;

      try {
        sessionStorage.setItem(FORM_SCROLL_KEY, String(scrollY));
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      } catch (error) {}
    });
  },
  true
);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      console.log('change', event);
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      console.log('event', event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      console.log('radio', target);
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      console.log('selectedSwatchValue', selectedSwatchValue);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



/**
 * 
 * CUSTOM CODE
 * 
 */
const productImagesPagination = {
  sliderInstances: new WeakMap(),

  getTrackableSlides(imagesContainer) {
    const slides = Array.from(imagesContainer.querySelectorAll('.product__images-wrapper'));
    return slides.filter((slide) => {
      const styles = window.getComputedStyle(slide);
      return styles.display != 'none' && styles.visibility != 'hidden';
    });
  },

  syncZoomButtons(imagesContainer) {
    const slides = Array.from(imagesContainer.querySelectorAll('.product__images-wrapper'));
    let zoomIndex = 0;

    slides.forEach((slide) => {
      const button = slide.querySelector('.product__media-zoom-button');
      if (!button) return;

      if (slide.querySelector('video')) return;

      button.dataset.slideIndex = String(zoomIndex);
      zoomIndex += 1;
    });
  },

  destroySlider(imagesContainer) {
    const slider = this.sliderInstances.get(imagesContainer);
    if (!slider) return;

    slider.destroy(true, true);
    this.sliderInstances.delete(imagesContainer);
  },

  setupContainer(imagesContainer) {
    if (typeof Swiper == 'undefined') return;

    const pagination = imagesContainer.querySelector('.product__images-pagination');
    const slides = this.getTrackableSlides(imagesContainer);
    this.syncZoomButtons(imagesContainer);
    const previousSlider = this.sliderInstances.get(imagesContainer);
    const previousIndex = previousSlider ? previousSlider.activeIndex : 0;

    if (!pagination || !slides.length) {
      this.destroySlider(imagesContainer);
      return;
    }

    this.destroySlider(imagesContainer);

    const slider = new Swiper(imagesContainer, {
      direction: 'vertical',
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 450,
      initialSlide: Math.min(previousIndex, slides.length - 1),
      threshold: 0,
      touchReleaseOnEdges: true,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: true,
        sensitivity: 0.85
      },
      pagination: {
        el: pagination,
        clickable: true,
        bulletClass: 'product__images-dot',
        bulletActiveClass: 'is-active'
      },
      on: {
        init: () => {
          pagination.hidden = slides.length <= 1;
        },
        resize: () => {
          this.syncZoomButtons(imagesContainer);
        }
      }
    });

    this.sliderInstances.set(imagesContainer, slider);
  },

  init(scope = document) {
    const containers = scope.querySelectorAll?.('.product__images');
    if (!containers?.length) return;
    containers.forEach((container) => this.setupContainer(container));
  }
};

window.rebuildProductImagesPagination = (scope = document) => {
  productImagesPagination.init(scope);
};

if (document.readyState == 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.rebuildProductImagesPagination(document);
  });
} else {
  window.rebuildProductImagesPagination(document);
}

window.addEventListener('pageshow', () => {
  window.rebuildProductImagesPagination(document);
});

customElements.define('footer-languages', class FooterLanguages extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.languages = this.querySelectorAll('input[type="radio"]');
    this.languages.forEach(language => {
      language.addEventListener('change', (e)=>{
        this.onSubmit(e);
      });
    });

    this.shopDomain = (typeof orbito !== 'undefined' && orbito.getCountryDomain) ? (orbito.getCountryDomain() || window.location.hostname.replace(/^www\./, '')) : window.location.hostname.replace(/^www\./, '');

    this.querySelector('select')?.addEventListener('change', (e)=>{
      this.onSubmit(e);
    });
  }

  onSubmit(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    const value = event.currentTarget.value;

    // Determine if it is a country_code or language_code change
    const inputCountry = form.querySelector('select[name="country_code"]');
    const inputLanguage = form.querySelector('input[name="locale_code"]');


    if (inputCountry) {
        // Update the Orbe cookie for country code
        document.cookie = `mdApp_countryCodeDomain=${value}; domain=${this.shopDomain}; path=/; max-age=${60 * 60 * 24 * 60};`;

    } 
    
    if (inputLanguage) {
        // Update the Orbe cookie for language code
        document.cookie = `mdApp_showRecommendationLang=${value}; domain=${this.shopDomain}; path=/; max-age=${60 * 60 * 24 * 60};`;
    }

    // Update the form's input field and submit the form
    if (form) form.submit();
  }
});

customElements.define('header-group', class HeaderGroup extends HTMLElement {
  constructor() {
    super();

    this.header = this.querySelector('header');
    this.isTransparent = this.header.classList.contains('header--transparent');
    this.menuItems = Array.from(this.querySelectorAll('.header__menu-item'));
    this.activeMenuItem = null;

    this.menuItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        this.handleMenuItemHover(item);
      });
    });

    this.header.addEventListener('mouseleave', () => {
      this.clearActiveMenuItem();
    });

    this.isTransparent && window.addEventListener('scroll', (e)=>{
      window.scrollY < 20 ? this.header.classList.add('header--transparent') : this.header.classList.remove('header--transparent');
    });
  }

  handleMenuItemHover(item) {
    const hasMegamenu = item.querySelector('.header__megamenu');
    if (!hasMegamenu) {
      this.clearActiveMenuItem();
      return;
    }

    if (this.activeMenuItem && this.activeMenuItem !== item) {
      this.activeMenuItem.classList.remove('is-megamenu-active');
    }

    item.classList.add('is-megamenu-active');
    this.activeMenuItem = item;
  }

  clearActiveMenuItem() {
    if (!this.activeMenuItem) return;
    this.activeMenuItem.classList.remove('is-megamenu-active');
    this.activeMenuItem = null;
  }

  connectedCallback() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    const headerInnerHeight = this.querySelector('header').offsetHeight;
    document.documentElement.style.setProperty('--header-inner-height', `${headerInnerHeight}px`);

    //Check distance from first menu to left
    const firstMenu = this.querySelector('.header__menus .header__menu-list:first-child');
    const left = firstMenu.offsetLeft;
    document.documentElement.style.setProperty('--header-left', `${left}px`);
  }
});


customElements.define('mobile-menu', class MobileMenu extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('.mobile-menu-button');
    this.content = this.querySelector('.mobile-menu-content');
    this.details = this.content.querySelectorAll('details');
    this.active = false;
  }

  connectedCallback() {
    this.button.addEventListener('click', (e)=>{
      this.active = !this.active;
      if (this.active) {
        document.dispatchEvent(new CustomEvent('mobile-menu:opened'));
      }
      this.closest('.header').classList.toggle('header--opened', this.active);
      document.querySelector('header').classList.remove('header--transparent');
      document.body.classList.toggle('overflow-hidden');
      this.button.classList.toggle('active', this.active);
      this.content.classList.toggle('hidden');
    });

    this.details.forEach(detail=>{
      detail.addEventListener('toggle', (e)=>{
      if (detail.open) {
        // Cierra el resto de desplegables
        this.details.forEach(otherDetail => {
          if (otherDetail !== detail) {
            otherDetail.removeAttribute('open');
            const summary = otherDetail.querySelector('summary');
            if (summary && window.innerWidth > 990) summary.classList.add('text-regular--low');
          }
        });
        // Remueve la clase del summary que se abrió
        const curSummary = detail.querySelector('summary');
        if (curSummary) curSummary.classList.remove('text-regular--low');
        // Oculta la clase de los links parent
        const parentLinks = this.content.querySelectorAll('.mobile-menu-content__menu-link--parent');
        window.innerWidth > 990 && parentLinks.forEach(link => link.classList.add('text-regular--low'));
      } else {
        // Al cerrar, si ninguno está abierto, quitar clase a todos los summaries
        const anyOpen = Array.from(this.details).some(d => d.open);
        if (!anyOpen) {
          this.details.forEach(d => {
            const summary = d.querySelector('summary');
            if (summary) summary.classList.remove('text-regular--low');
          });
          // Oculta la clase de los links parent
          const parentLinks = this.content.querySelectorAll('.mobile-menu-content__menu-link--parent');
          parentLinks.forEach(link => link.classList.remove('text-regular--low'));
        }
      }
      });
    });
  }

  close() {
    if (!this.active) return;

    this.active = false;
    this.closest('.header').classList.remove('header--opened');
    document.body.classList.remove('overflow-hidden');
    this.button.classList.remove('active');
    this.content.classList.add('hidden');
  }
});

customElements.define('header-search', class HeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.open = false;
    this.modal = this.querySelector('.header-search-modal');
    this.openButton = this.querySelector('.header-search-button');
    this.closeButton = this.querySelector('.js-close');
    this.header = document.querySelector('header');
    this.isTransparentHeader = this.header.classList.contains('header--transparent');

    this.openButton.addEventListener('click', (e)=>{
      this.open = !this.open;
      closeHeaderInteractivePanels({ closeMobileMenu: true });
      this.modal.classList.toggle('hidden', !this.open);
      this.querySelector('input').focus();
      document.body.classList.add('overflow-hidden');
      this.header.classList.toggle('header--opened', this.open);
      this.isTransparentHeader && this.header.classList.remove('header--transparent');
      
    });

    this.closeButton.addEventListener('click', (e)=>{
      this.close();
    });

    document.addEventListener('mobile-menu:opened', () => {
      this.close();
    });
  }

  close() {
    this.open = false;
    this.modal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    this.header.classList.remove('header--opened');
    this.querySelector('predictive-search')?.close(true);
    this.isTransparentHeader && this.header.classList.add('header--transparent');
  }
});

customElements.define('home-slider', class HomeSlider extends HTMLElement {
  constructor() {
    super();

    this.blocks = this.querySelectorAll('.slider__block');
    this.toggleBlocks = this.querySelectorAll('.js-toggle-block');

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 5000,
      },
      effect: "fade",
      on: {
        slideChangeTransitionStart: (swiper) => {
          this.blocks.forEach(block => {
            block.classList.remove('active');
          });
          this.toggleBlocks.forEach(block => {
            block.classList.remove('active');
          });
          this.blocks[swiper.realIndex].classList.add('active');
          this.toggleBlocks[swiper.realIndex].classList.add('active');
        }
      }
    });

    this.blocks.forEach(block => {
      block.addEventListener('click', (e) => {
        this.swiper.slideTo(parseInt(block.dataset.index) - 1);
      });
    });
  }
});

customElements.define('faqs-section', class FaqsSection extends HTMLElement {
  constructor() {
    super();

    this.sectionBlocks = this.querySelectorAll('.faqs__block');
    this.sidebarItems = this.querySelectorAll('.faqs__sidebar-item');
    if(window.innerWidth < 990){
      this.handleMobile()
    }
    this.handleDesktop();
    
  }

  handleDesktop(){
    this.sidebarItems.forEach(item => {
      item.addEventListener('click', (e)=>{
        const id = item.href.split('#')[1];
        this.sectionBlocks.forEach(block => {
          if(block.dataset.id === id){
            window.scrollTo({
              top: block.getBoundingClientRect().top + window.scrollY - 150,
              behavior: 'smooth'
            });
          }
        });
      });
    });
  }

  handleMobile(){
    //hide all elements
    this.sectionBlocks.forEach(block => {
      block.querySelectorAll('details').forEach(detail => {
        detail.classList.add('hidden');
      });

      //Add opening event listener
      block.addEventListener('click', (e)=>{
        if(e.target.closest('details')){
          return
        }
        if(Array.from(block.querySelectorAll('details')).some(detail => detail.classList.contains('hidden'))){
          block.querySelectorAll('details').forEach(detail => {
            detail.classList.remove('hidden');
          });
        } else {
          block.querySelectorAll('details').forEach(detail => {
            detail.classList.add('hidden');
          });
        }
      })
    });
    this.sectionBlocks[0].querySelectorAll('details').forEach(item=>{
      item.classList.remove('hidden');
    });
  }
});

customElements.define('archive-section', class ArchiveSection extends HTMLElement {
  constructor() {
    super();

    this.slides = this.querySelectorAll('.archive__content-slider__item');
    
    this.background = this.querySelector('.archive__background picture');
    this.rightContent = this.querySelector('.archive__content-header__right');
    this.footerTitle = this.querySelector('.archive__content-footer .w-400');

    this.swiper = new Swiper(this.querySelector('.swiper'), {  
      slidesPerView: 8.5,
      spaceBetween: 2,
      freeMode: true,
      centeredSlides: true,
      mousewheel: true,
      // navigation: {
      //   nextEl: this.querySelector('.slider-next'),
      //   prevEl: this.querySelector('.slider-prev'),
      // },
      breakpoints: {
        990: {
          slidesPerView: 28,
          centeredSlides: false,
          spaceBetween: 5,
        }
      }
    });

    this.slides.forEach((slide) => {
      this.handleSlideHover(slide);
    });
  }


  handleSlideHover(slide) {
    const event = window.innerWidth > 990 ? 'mouseenter' : 'click';
    slide.addEventListener(event, (e) => {
      if (window.innerWidth <= 989 && this.swiper) {
        this.swiper.slideTo(Number(slide.dataset.index));
      }

      const backgroundImage = slide.dataset.backgroundImage;
      const backgroundImageMobile = slide.dataset.backgroundImageMobile;
      const collabTitle = slide.dataset.collabTitle;
      const collabText = slide.dataset.collabText;

      this.background.querySelector('img').src = backgroundImage;
      this.background.querySelectorAll('source')[0].srcset = backgroundImage;
      this.background.querySelectorAll('source')[1].srcset = backgroundImageMobile;

      this.rightContent.querySelector('.text-regular.w-400').textContent = collabTitle;
      this.rightContent.querySelector('.text-regular:not(.w-400)').textContent = collabText;

      if (this.footerTitle) {
        this.footerTitle.textContent = collabTitle;
      }
    });
  }

});


if (!customElements.get('people-slider')) {
  customElements.define('people-slider', class PeopleSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.mediaQuery = window.matchMedia('(max-width: 1350px)');
      this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    connectedCallback() {
      this.init();
      this.mediaQuery.addEventListener('change', this.handleMediaChange);
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      this.destroySwiper();
    }

    handleMediaChange() {
      this.init();
    }

    init() {
      if (typeof Swiper === 'undefined') return;

      if (this.shouldUseSwiper()) {
        this.initSwiper();
      } else {
        this.destroySwiper();
      }
    }

    shouldUseSwiper() {
      return this.mediaQuery.matches || this.querySelectorAll('.people__item').length > 4;
    }

    initSwiper() {
      if (this.swiper) return;

      this.swiper = new Swiper(this, {
        slidesPerView: 1.17,
        spaceBetween: 0,
        freeMode: {
          enabled: true,
          momentum: true,
        },
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        watchOverflow: true,
        breakpoints: {
          990: {
            slidesPerView: 2.5,
          },
          1201: {
            slidesPerView: 4.2,
          },
        },
      });
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  });
}


customElements.define('infinite-scroll', class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.nextUrl = this.dataset.nextUrl;
    this.sectionId = this.dataset.sectionId;
    this.loaded = false;
    this.observer = null;
  }

  connectedCallback() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only proceed if not yet loaded and is intersecting
        if (entry.isIntersecting && !this.loaded) {
          fetch(this.nextUrl + `&section_id=${this.sectionId}`)
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              this.closest('.collection').querySelector('.collection__content').innerHTML += doc.querySelector('.collection__content').innerHTML;
              if(doc.querySelector('infinite-scroll')){
                this.nextUrl = doc.querySelector('infinite-scroll').dataset.nextUrl;
                this.setAttribute('data-next-url', this.nextUrl);
              } else {
                this.observer.unobserve(this);
                this.remove()
              }
            });
        }
      });
    });

    this.observer.observe(this);
  }
});



customElements.define('collection-view-toggler', class CollectionViewToggler extends HTMLElement {
  constructor() {
    super();

    this.activeView = this.dataset.type;
    
    this.addEventListener('click', (e)=>{
      this.updateLayout()
    });
  }
  
  
  updateLayout() {
    const searchParams = new URLSearchParams(window.location.search);
    const sectionId = this.activeView === "regular" ? "collection-quick-view" : this.closest('.collection').dataset.sectionId;
    fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      this.closest('.collection').querySelector('.collection__content-wrapper').outerHTML = doc.querySelector('.collection__content-wrapper').outerHTML;
      this.outerHTML = doc.querySelector('collection-view-toggler').outerHTML;
    });
  }
});


customElements.define('search-view-toggler', class SearchViewToggler extends HTMLElement {
  constructor() {
    super();

    this.activeView = this.dataset.type;
    
    this.addEventListener('click', (e)=>{
      this.updateLayout()
    });
  }
  
  
  updateLayout() {
    const searchParams = new URLSearchParams(window.location.search);
    const sectionId = this.activeView === "regular" ? "search-quick-view" : "main-search";
    fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      this.closest('.collection').querySelector('.collection__content-wrapper').outerHTML = doc.querySelector('.collection__content-wrapper').outerHTML;
      this.outerHTML = doc.querySelector('search-view-toggler').outerHTML;
    });
  }
});



customElements.define('custom-filters-form', class CustomFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.sectionId = this.closest('.collection').dataset.sectionId;
    this.inputs = this.form.querySelectorAll('input[type="checkbox"]');
    this.wrapper = this.closest('.collection-filters');


    this.submitButton.addEventListener('click', (e)=>{
      e.preventDefault();
      this.handleSubmit();
    });

    this.inputs.forEach(input => {
      input.addEventListener('change', (e)=>{
        //this.handleInputChange(e);
      });
    });

    this.wrapper.addEventListener('click', (e)=>{
      if(!e.target.closest('.custom-filters-form')){
        this.close();
      }
    });

    document.addEventListener('mobile-menu:opened', () => {
      this.close();
    });
  }

  handleInputChange(e) {
    e.target.closest('.custom-filter__options').querySelectorAll('input[type="checkbox"]').forEach(input => {
      if(input !== e.target){
        input.checked = false;
      }
    })
  }

  handleSubmit() {
    const searchParams = new URLSearchParams(window.location.search);

    const filterGroups = Array.from(this.form.querySelectorAll('.custom-filter'));

    filterGroups.forEach((group) => {
      const paramName = group.dataset.filterParam;
      if (!paramName) return;

      const checkedInputs = Array.from(group.querySelectorAll('input[type="checkbox"]:checked'));

      searchParams.delete(paramName);

      if (!checkedInputs.length) return;

      checkedInputs.forEach((input) => {
        searchParams.append(paramName, input.value);
      });
    });
    
    fetch(`${window.location.pathname}?section_id=${this.sectionId}&${searchParams.toString()}`)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      this.closest('.collection').querySelector('.collection__content-wrapper').outerHTML = doc.querySelector('.collection__content-wrapper').outerHTML;
      this.closest('.collection-filters').outerHTML = doc.querySelector('.collection-filters').outerHTML;
      window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
      this.close()
    });
  }

  close(){
    this.closest('details').removeAttribute('open');
  }
});

function closeHeaderInteractivePanels({ closeMobileMenu = false } = {}) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.close === 'function') {
    cartDrawer.close();
  }

  const cartNotification = document.querySelector('cart-notification');
  if (cartNotification && typeof cartNotification.close === 'function') {
    cartNotification.close();
  }

  document.querySelectorAll('.collection-filters details[open]').forEach((detail) => {
    detail.removeAttribute('open');
  });

  if (!closeMobileMenu) return;

  const mobileMenu = document.querySelector('mobile-menu');
  if (mobileMenu && typeof mobileMenu.close === 'function') {
    mobileMenu.close();
  }
}

document.addEventListener('mobile-menu:opened', () => {
  closeHeaderInteractivePanels();
});


customElements.define('product-dropdown', class ProductDropdown extends HTMLElement {
  constructor() {
    super();

    this.innerHeight = this.querySelector('.product-dropdown__item-content').offsetHeight;
    this.isOpen = false;
    this.productDropdowns = this.closest('product-dropdowns');

    this.addEventListener('click', (e)=>{
      if(e.target.closest('details')){
        setTimeout(() => {
          this.adaptHeight();
        }, 100);
        return;
      }
      this.toggle();
    });
  }

  toggle(){
    this.isOpen ? this.close() : this.open();
  }

  adaptHeight(){
    this.innerHeight = this.querySelector('.product-dropdown__item-content').offsetHeight;
    this.querySelector('.product-dropdown__item-content-wrapper').style.height = `${this.innerHeight}px`;
  }

  open(){
    this.productDropdowns.querySelectorAll('product-dropdown').forEach(dropdown => {
      dropdown.close();
    });
    this.isOpen = true;
    this.classList.add('open');
    this.querySelector('.product-dropdown__item-content-wrapper').style.height = `${this.innerHeight}px`;
    this.productDropdowns.querySelector('.product__info-dropdowns__content').innerHTML = this.querySelector('.product-dropdown__item-content').innerHTML;
  }

  close(){
    this.isOpen = false;
    this.classList.remove('open');
    this.querySelector('.product-dropdown__item-content-wrapper').style.height = '0px';
  }
});

customElements.define('product-page', class ProductPage extends HTMLElement {
  constructor() {
    super();

    this.productImages = this.querySelector('.product__images');
    
    
    this.handleScroll();
  }

  handleScroll(){
    if(window.innerWidth > 990)return;


    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          document.querySelector('.header').classList.remove('scrolled');
          document.querySelector('.breadcrumbs-section').classList.remove('scrolled');
        } else {
          document.querySelector('.header').classList.add('scrolled');
          document.querySelector('.breadcrumbs-section').classList.add('scrolled');
        }
      });
    }, {
      threshold: 0.1
    });

    observer.observe(this.productImages);
  }
});


customElements.define('product-dropdowns', class ProductDropdowns extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = this.querySelectorAll('product-dropdown');
    window.innerWidth < 990 && this.handleMobile();
  }

  handleMobile(){
   this.querySelector('.product__info-dropdowns__content').innerHTML = this.dropdowns[0].querySelector('.product-dropdown__item-content').innerHTML
   this.dropdowns[0].classList.toggle('open');
  }
});


customElements.define('sticky-atc', class StickyAtc extends HTMLElement {
  constructor() {
    super();

    this.init()
  }

  init(){
    document.body.style.setProperty('--sticky-atc-height', this.offsetHeight + 'px');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if(!entry.isIntersecting){
          this.classList.add('visible');
        } else {
          this.classList.remove('visible');
        }
      });
    });

    observer.observe(document.querySelector('.product__info .product-form'));
  }
});


customElements.define('product-media-zoom', class ProductMediaZoom extends HTMLElement {
  constructor() {
    super();

    this.thumbs = this.querySelectorAll('.product-media-zoom__thumb');
    this.items = this.querySelectorAll('.product-media-zoom__item');
    this.activeIndex = 0;
    this.closeButton = this.querySelector('.product-media-zoom__close');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e)=>{
        const targetThumb = e.currentTarget;
        this.toggle(parseInt(targetThumb.dataset.slideIndex, 10));
      });
    });

    document.addEventListener('click', (e) => {
      const activator = e.target.closest('.product__media-zoom-button');
      if (!activator) return;

      this.activeIndex = parseInt(activator.dataset.slideIndex, 10) || 0;
      this.open();
      this.toggle(this.activeIndex);
    });


    this.closeButton.addEventListener('click', (e)=>{
      this.close();
    });

    this.items.forEach((item) => {
      const media = item.querySelector('img, video');
      if (!media) return;
      media.addEventListener('load', () => this.centerItem(item));
      media.addEventListener('loadeddata', () => this.centerItem(item));
    });
  }

  toggle(index){
    if (!this.items.length || !this.thumbs.length) return;
    const boundedIndex = Math.max(0, Math.min(index, this.items.length - 1));
    const activeItem = this.querySelector(`.product-media-zoom__item[data-slide-index="${boundedIndex}"]`) || this.items[boundedIndex];
    const activeThumb = this.querySelector(`.product-media-zoom__thumb[data-slide-index="${boundedIndex}"]`) || this.thumbs[boundedIndex];

    this.items.forEach(item => {
      item.classList.add('hidden');
    });
    activeItem?.classList.remove('hidden');

    //Check if selected slide has a video and play it, else pause all videos
    if(activeItem?.querySelector('video')){
      activeItem.querySelector('video').play();
    } else {
      this.items.forEach(item => {
        if(item.querySelector('video')){
          item.querySelector('video').pause();
        }
      });
    }

    this.thumbs.forEach(thumb => {
      thumb.classList.remove('active');
    });
    activeThumb?.classList.add('active');

    this.activeIndex = boundedIndex;
    this.centerItem(activeItem);
  }

  close(){
    this.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  }

  open(){
    this.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  }

  centerItem(item){
    if (!item || item.classList.contains('hidden')) return;

    const applyCenter = () => {
      const maxScrollLeft = item.scrollWidth - item.clientWidth;
      const maxScrollTop = item.scrollHeight - item.clientHeight;
      item.scrollLeft = maxScrollLeft > 0 ? maxScrollLeft / 2 : 0;
      item.scrollTop = maxScrollTop > 0 ? maxScrollTop / 2 : 0;
    };

    const scheduleCenter = () => {
      requestAnimationFrame(() => {
        applyCenter();
        requestAnimationFrame(applyCenter);
      });
    };

    scheduleCenter();
    setTimeout(scheduleCenter, 50);
    setTimeout(scheduleCenter, 200);
  }
});

customElements.define('main-menu-item', class MainMenuItem extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (e)=>{
      this.querySelector('.header__megamenu-submenu').classList.remove('hidden');
      Array.from(this.parentElement.children).forEach(child => {
        if(child !== this){
          console.log(child)
          child.classList.add('opacity-50');
        }
      });
    });

    // Hide the submenu if clicking outside of it
    const handleDocumentClick = (e) => {
      const submenu = this.querySelector('.header__megamenu-submenu');
      if (
        !this.contains(e.target) && 
        !submenu.classList.contains('hidden')
      ) {
        submenu.classList.add('hidden');
        Array.from(this.parentElement.children).forEach(child => {
          if(child !== this){
            console.log(child)
            child.classList.remove('opacity-50');
          }
        });
        document.removeEventListener('click', handleDocumentClick, true);
      }
    };

    this.addEventListener('click', (e) => {
      // When the item is opened (submenu shown), set up click-away to close it
      setTimeout(() => {
        document.addEventListener('click', handleDocumentClick, true);
      }, 0);
    });
  }
});


customElements.define('mobile-menu-item', class MobileMenuItem extends HTMLElement {
  constructor() {
    super();

    this.menuId = this.dataset.menuId;
    this.opened = false;
    this.innerMenu = this.querySelector('.mobile-menu-content__menu-list[data-menu-id="' + this.menuId + '"]');
    this.closeButton = this.querySelector('.js-close-menu-item[data-menu-id="' + this.menuId + '"]');
    this.openButton = this.querySelector('.js-open-menu-item[data-menu-id="' + this.menuId + '"]');

    
    this.openButton?.addEventListener('click', (e)=>{
      !this.opened && this.toggle();
    });
    
    this.closeButton?.addEventListener('click', (e)=>{
      this.close();
    });
  }

  toggle(){
    this.innerMenu.classList.toggle('mobile-menu-content__menu-list--hidden');
    this.opened = !this.opened;
  }

  close(){
    this.innerMenu.classList.add('mobile-menu-content__menu-list--hidden');
    this.opened = false;
  }
});



customElements.define('color-swatches', class ColorSwatches extends HTMLElement {
  constructor() {
    super();

    this.swatches = this.querySelectorAll('.color-swatch--');
    this.sectionId = this.dataset.sectionId;
    this.previousButton = this.querySelector('.color-swatches__arrow--prev');
    this.nextButton = this.querySelector('.color-swatches__arrow--next');
    this.swiper = null;

    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e)=>{
        fetch(swatch.dataset.colorSwatchProduct+"?section_id="+this.sectionId)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          document.querySelector('product-page').innerHTML = doc.querySelector('product-page').innerHTML;
          document.querySelector('product-media-zoom').outerHTML = doc.querySelector('product-media-zoom').outerHTML;
          document.querySelector('sticky-atc').outerHTML = doc.querySelector('sticky-atc').outerHTML;
          document.querySelector('sticky-atc').init()
          window.rebuildProductImagesPagination(document);
          window.history.pushState({}, '', swatch.dataset.colorSwatchProduct);
          if(window.innerWidth < 990){
            window.scrollTo(0, 0);
            setTimeout(() => {
              document.querySelector('header').classList.remove('scrolled');
            }, 100);
          }
        });
      });
    });


    this.initSwiper()
  }

  initSwiper(){
    if (window.innerWidth > 990) return;

    this.swiper = new Swiper(this, {
      slidesPerView: 6,
      spaceBetween: 4,
      navigation: {
        prevEl: this.previousButton,
        nextEl: this.nextButton,
        disabledClass: 'swiper-button-disabled',
      },
    });

    this.closest('sticky-atc').init();
  }
});


customElements.define('card-color-swatches', class CardColorSwatches extends HTMLElement {
  constructor() {
    super();

    this.productUrl = this.dataset.productUrl;
    this.sectionId = this.dataset.sectionId || 'card-color-swatches';
    this.previousButton = this.querySelector('.card-color-swatches__arrow--prev');
    this.nextButton = this.querySelector('.card-color-swatches__arrow--next');
    this.swatches = null
    this.init()
  }


  init(){
    fetch(this.productUrl.split('?')[0]+"?section_id="+this.sectionId)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      this.innerHTML = doc.querySelector('.card-color-swatches').innerHTML;
      this.swatches = this.querySelectorAll('.card-color-swatch');
      
      this.swatches.forEach(swatch => {
        swatch.addEventListener('mouseenter', (e)=>{
          this.handleSwatchClick(e, swatch);
        })
      })
      new Swiper(this, {
        slidesPerView: 5,
        spaceBetween: 4,
        navigation: {
          prevEl: '.card-color-swatches__arrow--prev',
          nextEl: '.card-color-swatches__arrow--next',
        }
      });
    });
  }

  handleSwatchClick(e, activeSwatch){
    e.preventDefault();
    fetch(activeSwatch.dataset.colorSwatchProduct+"?section_id=card-product")
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      this.swatches.forEach(swatch => {
        swatch.classList.remove('active');
      });
      activeSwatch.classList.add('active');
      this.closest('.card-product').querySelector('.card-product__images').outerHTML = doc.querySelector('.card-product__images').outerHTML;
      this.closest('.card-product').querySelector('.card-product__content').outerHTML = doc.querySelector('.card-product__content').outerHTML;
      this.closest('.card-product-link').setAttribute("href", doc.querySelector('.card-product-link').getAttribute('href'));
    });
  }
});

const mobileCardImageDetails = {
  timers: new WeakMap(),

  normalizeUrl(url) {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.origin);
      return parsedUrl.pathname.replace(/\/$/, '');
    } catch (error) {
      return url.split('?')[0].replace(/\/$/, '');
    }
  },

  getActiveSlide(container) {
    const slides = Array.from(container.querySelectorAll('a.card-product__image_inner[href]'));
    if (!slides.length) return null;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    return slides.reduce((closestSlide, slide) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(containerCenter - slideCenter);

      if (!closestSlide || distance < closestSlide.distance) {
        return { slide, distance };
      }

      return closestSlide;
    }, null)?.slide;
  },

  update(container) {
    if (window.innerWidth > 989) return;

    const activeSlide = this.getActiveSlide(container);
    if (!activeSlide) return;

    const card = container.closest('.card-product');
    const cardLink = container.closest('.card-product-link');
    const currentDetailsLink = card?.querySelector('.card-product__content');
    const activeUrl = activeSlide.getAttribute('href');

    if (!card || !cardLink || !activeUrl) return;
    if (this.normalizeUrl(activeUrl) === this.normalizeUrl(currentDetailsLink?.getAttribute('href'))) return;

    fetch(`${activeUrl.split('?')[0]}?section_id=card-product`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCard = doc.querySelector('.card-product');
        const newCardLink = doc.querySelector('.card-product-link');
        const newContent = newCard?.querySelector('.card-product__content');
        const currentContent = card.querySelector('.card-product__content');

        if (!newContent || !currentContent) return;

        currentContent.outerHTML = newContent.outerHTML;
        if (newCardLink) {
          cardLink.setAttribute('href', newCardLink.getAttribute('href'));
        }
      });
  },

  schedule(container) {
    window.clearTimeout(this.timers.get(container));
    this.timers.set(
      container,
      window.setTimeout(() => {
        this.update(container);
      }, 120)
    );
  },
};

document.addEventListener(
  'scroll',
  (event) => {
    const container = event.target?.closest?.('.card-product__image.large-up-hide');
    if (!container) return;

    mobileCardImageDetails.schedule(container);
  },
  true
);


customElements.define('back-in-stock-modal', class BackInStockModal extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('[role="dialog"]');
    this.form = this.querySelector('[data-bis-form]');
    this.successMessage = this.querySelector('[data-bis-success]');
    this.errorMessage = this.querySelector('[data-bis-error]');
    this.closeButtons = this.querySelectorAll('[data-bis-close]');
    this.variantChangeUnsubscriber = undefined;
    this.openedBy = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
  }

  connectedCallback() {
    if (this.isConnectedBound) return;
    this.isConnectedBound = true;

    this.form?.addEventListener('submit', this.onSubmit);
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('click', this.onOverlayClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant?.id) return;
      this.dataset.variantId = variant.id;
    });
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  open(opener) {
    const variantInput = opener?.closest('product-form')?.querySelector('input[name="id"]');
    if (variantInput?.value) {
      this.dataset.variantId = variantInput.value;
    }

    this.openedBy = opener;
    this.resetState();
    this.removeAttribute('hidden');
    this.querySelector('.back-in-stock-modal__description')?.classList.remove("hidden")
    this.querySelector('.back-in-stock-modal__title')?.classList.remove("hidden")
    document.body.classList.add('overflow-hidden');
    trapFocus(this, this.panel);
    this.querySelector('input[type="email"]')?.focus();
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.openedBy);
    this.openedBy = null;
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.close();
  }

  onOverlayClick(event) {
    if (event.target === this) {
      this.close();
    }
  }

  resetState() {
    this.form?.reset();
    this.form?.classList.remove('hidden');
    this.successMessage?.classList.add('hidden');
    this.errorMessage?.classList.add('hidden');
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
    }
  }

  getCompanyId() {
    const fromDataset = (this.dataset.companyId || '').trim();
    if (fromDataset) return fromDataset;

    const fromSettings = (window.klaviyoBackInStock?.companyId || '').trim();
    if (fromSettings) return fromSettings;

    const script = Array.from(document.scripts).find((node) => /company_id=/.test(node.src));
    if (!script) return '';

    return new URL(script.src, window.location.origin).searchParams.get('company_id') || '';
  }

  getMarketingListId() {
    const fromDataset = (this.dataset.marketingListId || '').trim();
    if (fromDataset) return fromDataset;

    return (window.klaviyoBackInStock?.marketingListId || '').trim();
  }

  getStrings() {
    return window.klaviyoBackInStock?.strings || {};
  }

  buildVariantCompositeId(variantId) {
    return `$shopify:::$default:::${variantId}`;
  }

  getRevision() {
    return window.klaviyoBackInStock?.revision || '2026-04-15';
  }

  getKlaviyoHeaders() {
    return {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      revision: this.getRevision(),
    };
  }

  async klaviyoClientRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getKlaviyoHeaders(),
      body: JSON.stringify(body),
    });

    if (response.ok) {
      return response;
    }

    const errorBody = await response.text().catch(() => '');
    throw new Error(`Klaviyo request failed (${response.status}): ${errorBody}`);
  }

  getLocale() {
    const fromDataset = (this.dataset.locale || '').trim();
    if (fromDataset) return fromDataset;

    const config = window.klaviyoBackInStock || {};
    if (config.locale) return config.locale;

    const language = (config.language || '').trim();
    const country = (config.country || '').trim();
    if (!language) return '';
    if (language.includes('-')) return language;
    if (country) return `${language}-${country}`;
    return language;
  }

  getProfileAttributes(email) {
    const attributes = { email };
    const exchangeId = document.cookie.match(/_kx=([^;]+)/);

    if (exchangeId) {
      attributes._kx = decodeURIComponent(exchangeId[1]);
    }

    return attributes;
  }

  getMarketingProfileAttributes(email) {
    const attributes = this.getProfileAttributes(email);
    const locale = this.getLocale();

    if (locale) {
      attributes.locale = locale;
    }

    return attributes;
  }

  identifyWithKlaviyoOnsite(email) {
    if (typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.identify === 'function') {
      window.klaviyo.identify({ email });
      return;
    }

    if (Array.isArray(window._learnq)) {
      window._learnq.push(['identify', { $email: email }]);
    }
  }

  async createOrUpdateProfile(companyId, email) {
    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'profile',
          attributes: this.getProfileAttributes(email),
        },
      }
    );
  }

  async onSubmit(event) {
    event.preventDefault();

    const emailInput = this.form?.querySelector('input[type="email"]');
    const marketingOptIn = this.form?.querySelector('input[name="marketing_opt_in"]')?.checked;
    const submitButton = this.form?.querySelector('[type="submit"]');
    const email = emailInput?.value.trim();
    const strings = this.getStrings();
    const companyId = (this.getCompanyId() || '').trim();
    const variantId = this.dataset.variantId;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError(strings.emailError);
      emailInput?.focus();
      return;
    }

    if (!companyId || !variantId) {
      this.showError(strings.error);
      return;
    }

    submitButton?.setAttribute('aria-disabled', 'true');

    try {
      const marketingListId = (this.getMarketingListId() || 'RBhrvs').trim();

      if (!marketingListId) {
        console.error('Klaviyo marketing list ID is not configured. Set it in Theme settings → Klaviyo.');
        this.showError(strings.error);
        return;
      }

      await this.createMarketingSubscription(companyId, email, marketingListId, marketingOptIn);

      this.identifyWithKlaviyoOnsite(email);
      await this.createBackInStockSubscription(companyId, email, variantId);

      this.showSuccess();
    } catch (error) {
      console.error(error);
      this.showError(strings.error);
    } finally {
      submitButton?.removeAttribute('aria-disabled');
    }
  }

  async createBackInStockSubscription(companyId, email, variantId) {
    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: this.getProfileAttributes(email),
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: this.buildVariantCompositeId(variantId),
              },
            },
          },
        },
      }
    );
  }

  async createMarketingSubscription(companyId, email, listId, hasMarketingConsent) {
    const profileAttributes = this.getMarketingProfileAttributes(email);

    if (hasMarketingConsent) {
      profileAttributes.subscriptions = {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      };
    } else {
      profileAttributes.subscriptions = {};
    }

    await this.klaviyoClientRequest(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock modal',
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      }
    );
  }

  showSuccess() {
    this.form?.classList.add('hidden');
    this.querySelector('.back-in-stock-modal__description')?.classList.add("hidden")
    this.querySelector('.back-in-stock-modal__title')?.classList.add("hidden")
    this.errorMessage?.classList.add('hidden');
    this.successMessage?.classList.remove('hidden');
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message || this.getStrings().error || '';
    this.errorMessage.classList.remove('hidden');
  }
});

customElements.define('product-materials', class ProductMaterials extends HTMLElement {
  constructor() {
    super();

    this.materials = this.querySelectorAll('.product-materials__thumb');
    this.init()
  }

  init(){
    this.materials.forEach(material => {
      material.addEventListener('click', (e)=>{
        this.handleMaterialClick(e, material);
      });
    });
  }

  handleMaterialClick(e, activeMaterial){
    e.preventDefault();
    this.materials.forEach(material => {
      material.classList.remove('active');
    });
    activeMaterial.classList.add('active');
    this.querySelectorAll('.product-materials__info').forEach(info => {
      info.classList.add('hidden');
    });
    this.querySelectorAll('.product-materials__right div').forEach(item => {
      item.classList.add('hidden');
    });
    this.querySelector('.product-materials__info[data-material-id="' + activeMaterial.dataset.materialId + '"]').classList.remove('hidden');
    this.querySelector('.product-materials__right [data-material-id="' + activeMaterial.dataset.materialId + '"]').classList.remove('hidden');
  }
});